import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Vote } from "lucide-react";

export default function EVotingPage() {
  const { propertyId } = useProperty();
  const [items, setItems] = useState([]);
  const load = async () => { const { data } = await api.get(`/votes?property_id=${propertyId}`); setItems(data.items); };
  useEffect(() => { if (propertyId) load(); }, [propertyId]);
  const cast = async (vid, option) => {
    try {
      await api.post(`/votes/${vid}/cast`, { option });
      toast.success(`Vote cast: ${option}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vote failed");
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Governance" title="E-Voting & Proxies" subtitle="Have your voice heard." />
      <div className="space-y-4">
        {items.map((v) => {
          const total = Object.values(v.tally || {}).reduce((a, b) => a + b, 0) || 1;
          return (
            <Card key={v.id} className="p-6 bg-white">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="overline">Ballot open</div>
                  <div className="text-xl font-semibold font-display mt-1">{v.title}</div>
                  <div className="text-xs text-zinc-500 mt-1">Closes {new Date(v.closes_at).toLocaleDateString()}</div>
                </div>
                <Vote className="h-6 w-6 text-zinc-500" />
              </div>
              <div className="mt-4 grid grid-cols-1 gap-3">
                {v.options.map((opt) => {
                  const count = (v.tally || {})[opt] || 0;
                  const pct = Math.round((count / total) * 100);
                  return (
                    <div key={opt} className="rounded-md border border-black/5 p-3">
                      <div className="flex items-center justify-between">
                        <div className="font-medium">{opt}</div>
                        <div className="text-xs text-zinc-500">{count} votes · {pct}%</div>
                      </div>
                      <div className="h-1.5 bg-zinc-100 rounded-full mt-2 overflow-hidden">
                        <div className="h-full bg-zinc-900" style={{ width: pct + "%" }} />
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cast(v.id, opt)}
                        data-testid={`vote-${v.id}-${opt}`}
                        className="rounded-full mt-3"
                      >Cast for “{opt}”</Button>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
        {items.length === 0 && <div className="text-sm text-muted-foreground">No open ballots.</div>}
      </div>
    </div>
  );
}
